import React, { useState } from 'react'
import { X, Loader2 } from 'lucide-react'
import { useProaStore } from '@/stores/proa-store'
import { useCreateScenario, useListScenarios } from '../hooks/useProaData'

interface CreateScenarioModalProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Modal for creating a new PROA scenario
 */
export function CreateScenarioModal({ isOpen, onClose }: CreateScenarioModalProps) {
  const { selectedYear, setSelectedScenario } = useProaStore()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [baseScenarioId, setBaseScenarioId] = useState<string>('')

  const { data: scenarios } = useListScenarios(selectedYear)
  const createScenario = useCreateScenario()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) return

    try {
      const newScenario = await createScenario.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        year: selectedYear,
        baseScenarioId: baseScenarioId || undefined,
      })

      // Select the newly created scenario
      setSelectedScenario(newScenario)

      // Reset form and close
      setName('')
      setDescription('')
      setBaseScenarioId('')
      onClose()
    } catch (error) {
      console.error('Failed to create scenario:', error)
    }
  }

  const handleClose = () => {
    setName('')
    setDescription('')
    setBaseScenarioId('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-[#1E2024] rounded-xl border border-[#2A2D34] w-full max-w-md mx-4 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#2A2D34]">
          <h2 className="text-lg font-semibold text-white">Criar Novo Cenario</h2>
          <button
            onClick={handleClose}
            className="p-1 rounded-lg hover:bg-[#2A2D34] transition-colors"
          >
            <X className="h-5 w-5 text-[#838998]" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Year (read-only) */}
          <div>
            <label className="block text-sm font-medium text-[#838998] mb-1">
              Ano
            </label>
            <div className="h-10 rounded-md border border-[#2A2D34] bg-[#24262B] px-3 py-2 text-sm text-white flex items-center">
              {selectedYear}
            </div>
          </div>

          {/* Name */}
          <div>
            <label htmlFor="scenario-name" className="block text-sm font-medium text-[#838998] mb-1">
              Nome do Cenario *
            </label>
            <input
              id="scenario-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Orcamento Oficial 2025"
              className="w-full h-10 rounded-md border border-[#2A2D34] bg-[#24262B] px-3 py-2 text-sm text-white placeholder:text-[#838998] focus:outline-none focus:ring-2 focus:ring-[#D2FA52] focus:ring-offset-2 focus:ring-offset-[#1E2024]"
              required
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="scenario-description" className="block text-sm font-medium text-[#838998] mb-1">
              Descricao
            </label>
            <textarea
              id="scenario-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descricao opcional do cenario..."
              rows={3}
              className="w-full rounded-md border border-[#2A2D34] bg-[#24262B] px-3 py-2 text-sm text-white placeholder:text-[#838998] focus:outline-none focus:ring-2 focus:ring-[#D2FA52] focus:ring-offset-2 focus:ring-offset-[#1E2024] resize-none"
            />
          </div>

          {/* Base Scenario (clone from) */}
          <div>
            <label htmlFor="base-scenario" className="block text-sm font-medium text-[#838998] mb-1">
              Clonar de (opcional)
            </label>
            <select
              id="base-scenario"
              value={baseScenarioId}
              onChange={(e) => setBaseScenarioId(e.target.value)}
              className="w-full h-10 rounded-md border border-[#2A2D34] bg-[#24262B] px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#D2FA52] focus:ring-offset-2 focus:ring-offset-[#1E2024]"
            >
              <option value="">Criar cenario vazio</option>
              {scenarios?.map((scenario) => (
                <option key={scenario.scenarioId} value={scenario.scenarioId}>
                  {scenario.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-[#838998]">
              Copiar metas de um cenario existente
            </p>
          </div>

          {/* Error */}
          {createScenario.error && (
            <div className="p-3 rounded-md bg-red-500/10 border border-red-500/20">
              <p className="text-sm text-red-400">
                Erro ao criar cenario: {createScenario.error.message}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-[#838998] hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!name.trim() || createScenario.isPending}
              className="px-4 py-2 text-sm font-medium bg-[#D2FA52] text-[#0D0E10] rounded-lg hover:bg-[#c2ea42] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {createScenario.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Criar Cenario
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateScenarioModal
